import { FormNavigationItem } from "app/modules/forms/components/navigation";
import { trySetProp } from "app/shared/global.functions";
import {
    AccessLevel,
    CommonBaseModel,
    FormPage,
    ProductDefinition,
    EntitlementSet,
    MetadataEntitlement,
    ProductMetadata,
    SurveyResult,
    Application,
    iApplyFormControl,
} from "app/shared/model";
import { DateTime } from "luxon";

/** Class representing the state of the data stored in the form service for the current application */
export class ApplicationState implements IApplicationState {
    formId: string | null = null;
    formVersion: number | null = null;
    applicationId: string | null = null;
    applicationIdDisplay: string | null = null;
    offlineApplicationId: string | null = null;
    applicationVersion: number | null = null;
    workflowState: string | null = null;
    workflowStateId: string | null = null;
    pageId: number = 0;
    subPageId: number | null = null;
    created: DateTime<boolean> | null = null;
    organisationId: string | null = null;
    readonly: boolean = false;

    constructor(data?: IApplicationState) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    isCurrentProduct(formId: string, formVersion: number): boolean {
        return this.formId === formId && this.formVersion === formVersion;
    }

    isCurrentApplication(compareApplicationId: string, compareApplicationVersion: number, allowVersionIncrement: boolean = false): boolean {
        let result = this.applicationId === compareApplicationId;

        result &&= this.applicationVersion === compareApplicationVersion ||
            (allowVersionIncrement && this.applicationVersion == (compareApplicationVersion + 1));

        return result;
    }

    isCurrentState(state: IApplicationState)
    {
        return this.applicationId == state.applicationId &&
            this.applicationVersion == state.applicationVersion &&
            this.formId == state.formId &&
            this.formVersion == state.formVersion &&
            // this.pageId == state.pageId &&
            // this.subPageId == state.subPageId &&
            this.workflowState == state.workflowState &&
            this.workflowStateId == state.workflowStateId;
    }

    updateFromApplication(application: Application): void {
        this.applicationId = application.id;
        this.applicationIdDisplay = application.applicationIdDisplay;
        this.offlineApplicationId = application.offlineApplicationId;
        this.applicationVersion = application.version;
        this.workflowState = application.applicationState;
        this.workflowStateId = null;  // application.workflowStateId;
        this.created = application.created;
        this.organisationId = application.organisationId;
        // this.organisationName = application.organisationName;
    }

    updateFromProduct(formId: string, formVersion: number): void {
        this.formId = formId;
        this.formVersion = formVersion;
    }
}

export class EmptyResult extends CommonBaseModel { }

export class AuthorisedStatesNames extends CommonBaseModel {
    success: boolean;
    authorisedStates?: string[];
}

export class Validator extends CommonBaseModel {
    id: string;
    archived: boolean;
    defaultMessageTemplate: string | null;
    description: string | null;
    name: string | null;
    regex: string | null;
    validatorSyncVersion: number;
    inputMaskDefinition: string | null;
    inputMaskReverseOption: string | null;
    isNumeric: boolean;
    prefix: string | null;
    suffix: string | null;
    precision: number | null;
    forceDecimals: boolean | null;
    useGroupSeparator: boolean;
    positiveOnly: boolean;
    negativeFormat: string | null;
    testCases: string[];

    init(_data?: any): this {
        super.init(_data);
        return this;
    }

    static fromJS(data: any): Validator {
        data = typeof data === 'object' ? data : {};
        return (new Validator()).init(data);
    }
}

export class ComposeHtmlContentMetadata extends CommonBaseModel {
    controlName: string;
    qualifier: string;
    result: string;
    status: string;

    init(_data?: any): this {
        super.init(_data);
        return this;
    }

    static fromJS(data: any): ComposeHtmlContentMetadata {
        data = typeof data === 'object' ? data : {};
        return (new ComposeHtmlContentMetadata()).init(data);
    }
}

export class StartMobileVerificationTimerResult extends CommonBaseModel {
    success: boolean;
    maxRetryAttemptsReached: boolean;
    timedOut: boolean;
    remainingTime: number;
}

export class GetMobileVerificationStatusResult extends CommonBaseModel {
    success: boolean;
    maxRetryAttemptsReached: boolean;
    timedOut: boolean;
}

export class VerifyMobileVerificationCodeResult extends CommonBaseModel {
    success: boolean;
    maxVerificationRetryReached: boolean;
    phoneNumber: string;
    restartVerification: boolean;
    maxRetryAttemptsReached: boolean;
    timedOut: boolean;
}

export class SendMobileVerificationCodeResult extends CommonBaseModel {
    success: boolean;
    maxRetryAttemptsReached: boolean;
    maxSendRetryReached: boolean;
    maxVerificationRetryReached: boolean;
    restartVerification: boolean;
    timedOut: boolean;
}

export class ComposeHtmlContentMetadataResult extends CommonBaseModel {
    success: boolean;
    returnData: ComposeHtmlContentMetadata[];

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        let returnObj = _data["returnData"];
        if (returnObj && Array.isArray(returnObj)) {
            let arr: ComposeHtmlContentMetadata[] = [];
            for (let item of returnObj) {
                arr.push((new ComposeHtmlContentMetadata()).init(item));
            }
            this.returnData = arr;
        }

        return this;
    }
}

export class CalculateAllResult extends CommonBaseModel {
    success: boolean;
    result: object;
    pageId: number;

    init(_data?: any): this {
        super.init(_data);
        return this;
    }

    static fromJS(data: any): CalculateAllResult {
        data = typeof data === 'object' ? data : {};
        return (new CalculateAllResult()).init(data);
    }
}

export class ValidatorListResult extends CommonBaseModel {
    success: boolean;
    updated: boolean;
    validatorSyncVersion: number;
    validators: Validator[];

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);

        let validObj = _data["Validators"];

        if (validObj && Array.isArray(validObj)) {
            let arr: Validator[] = [];
            for (let item of validObj) {
                arr.push((new Validator()).init(item));
            }
            this.validators = arr;
        }

        return this;
    }
}

export class SystemSettings extends CommonBaseModel {
    enableAuditLog: boolean;
    enableFormApprovals: boolean;
    enableMicrosoftCopilot: boolean;
    ignoreRegExRequired: boolean;
    serviceEndpointFilter: {
        Enabled: boolean,
        Whitelist: string[]
    }
    systemName: string;
    uris: object;
}

export class SystemSettingsResult extends CommonBaseModel {
    success: boolean;
    settings: SystemSettings;

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        this.settings = (new SystemSettings()).init(_data["Settings"]);

        return this;
    }
}

export interface IApplicationState {
    formId: string | null;
    formVersion: number | null;
    applicationId: string | null;
    applicationIdDisplay: string | null;
    offlineApplicationId: string | null;
    applicationVersion: number | null;
    workflowState: string | null;
    workflowStateId: string | null;
    pageId: number;
    subPageId: number | null;
    created: DateTime | null;
    organisationId: string | null;
    readonly: boolean;
}

// export getProductInput = (data: GetProductInput = {}): GetProductInput => ({ id, versionNumber, pageId, applicationId });

// export CreateUserPage = ({
//     pageType: FormPageType.User,
//     controls = [],
// } = {}): IFormPage => ({
//     pageType,
//     controls,
// });

export interface GetEntitlementsInput {
    formId: string;
    formVersion: number | null;
    pageId: number;
    applicationId: string;
}

export class GetProductListResponse extends CommonBaseModel {
    success: boolean = false;
    products: ProductMetadata[];

    init(_data?: any): this {
        super.init(_data);
        if (_data['Products']) {
            this.products = _data['Products'].map(item => (new ProductMetadata).init(item));
        }

        return this;
    }
}

export class GetProductDetailsResponse extends CommonBaseModel {
    success: boolean = false;
    metadata: ProductDefinition = null;

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        this.metadata = _data["metadata"] ? (new ProductDefinition()).init(_data["metadata"]) : <any>undefined;

        return this;
    }

    static fromJS(data: any): GetProductDetailsResponse {
        data = typeof data === 'object' ? data : {};
        let result = new GetProductDetailsResponse();
        result.init(data);
        return result;
    }
}

export class SurveyResultResponse extends CommonBaseModel {
    success: boolean;
    result: SurveyResult;

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        super.init(_data);
        this.result = _data["result"] ? (new SurveyResult()).init(_data["result"]) : undefined;
        return this;
    }

    static fromJS(data: any): SurveyResultResponse {
        data = typeof data === 'object' ? data : {};
        let result = new SurveyResultResponse();
        result.init(data);
        return result;
    }
}

export class GetProductResponse extends CommonBaseModel {
    success: boolean = false;
    metadata: ProductDefinition = null;
    paymentData: any[] = [];
    externalPopulatedFields: string[] = [];

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;
        super.init(_data);
        this.metadata = _data["metadata"] ? (new ProductDefinition()).init(_data["metadata"]) : undefined;
        super.initArray(this, "paymentData", _data["paymentData"]);
        return this;
    }

    static fromJS(data: any): GetProductResponse {
        data = typeof data === 'object' ? data : {};
        let result = new GetProductResponse();
        result.init(data);
        return result;
    }
}

export class GetProductMenuResponse extends CommonBaseModel {
    success: boolean;
    menu: FormNavigationItem[];

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        let menuVar = _data["menu"];
        this.menu = [];
        if (Array.isArray(menuVar)) {
            for (let item of menuVar) {
                this.menu!.push((new FormNavigationItem()).init(item));// ToDo
            }
        }

        return this;
    }

    static fromJS(data: any): GetProductMenuResponse {
        data = typeof data === 'object' ? data : {};
        let result = new GetProductMenuResponse();
        result.init(data);
        return result;
    }
}

/** Form Access Entitlements */
export class FormAccessEntitlements extends CommonBaseModel {
    baseAccess: AccessLevel;
    manualSaveAllowed: boolean;
    autoSaveEnabled: boolean;
    controlPermissions: EntitlementSet;
    summaryControlPermissions: { [summaryControlId: string]: FormPage[]; };
    metadataPermissions: MetadataEntitlement[] = [];

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);

        this.controlPermissions = _data["ControlPermissions"] ? (new EntitlementSet()).init(_data["ControlPermissions"]) : undefined;
        const metadataPermissions = _data["MetadataPermissions"] ?? _data["metadataPermissions"];
        this.metadataPermissions = Array.isArray(metadataPermissions)
                                            ? metadataPermissions.map(item => (new MetadataEntitlement()).init(item))
                                                                                        : [];

        let summaryControlPermissions = _data["SummaryControlPermissions"];
        this.summaryControlPermissions = {};
        if (summaryControlPermissions) {
            for (let key in summaryControlPermissions) {
                if (summaryControlPermissions.hasOwnProperty(key)) {
                    this.summaryControlPermissions[key] = [];
                    for (let item of summaryControlPermissions[key]) {
                        this.summaryControlPermissions[key].push(FormPage.getInitedPageDependingOnPageType(item));
                    }
                }
            }
        }

        return this;
    }
}

export class GetProductEntitlementsResponse extends CommonBaseModel {
    success: boolean;
    accessRights: FormAccessEntitlements;

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        this.accessRights = (_data["accessRights"] && (typeof _data["accessRights"] == 'object')) ?
            (new FormAccessEntitlements()).init(_data["accessRights"]) : <any>undefined;

        return this;
    }
}

export class GetProductSectionsCountResponse extends CommonBaseModel {
    success: boolean;
    sections: { [pageId: number]: number[] };

    init(_data?: any): this {
        return super.init(_data);
    }

    static fromJS(data: any): GetProductSectionsCountResponse {
        data = typeof data === 'object' ? data : {};
        let result = new GetProductSectionsCountResponse();
        result.init(data);
        return result;
    }
}

export class UploadFileResponse extends CommonBaseModel {
    success: boolean;
    applicationId!: string;
    attachmentId!: string;
    fileName!: string;
    handlers: { [key: string]: any; };

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        this.handlers = _data["Handlers"] ? _data["Handlers"] : null;

        return this;
    }
}

export class FileHandlerResponse implements IFileHandlerResponse {
    success: boolean = false;
    processed: boolean = false;
    fileValid: boolean = false;
    errors: FileHandlerResponseError[];
    messages: FileHandlerResponseMessage[];
    results: any[]; // FileHandlerResponseResult[];

    constructor(data?: IFileHandlerResponse) {
        if (data) {
            this.success = data.success;
            this.processed = data.processed;
            this.fileValid = data.fileValid;
            this.errors = data.errors;
            this.messages = data.messages;
            this.results = data.results;
        }
    }

    init(_data?: any): this {
        if (_data) {
            this.success = _data["Success"];
            this.processed = _data["Processed"];
            this.fileValid = _data["FileValid"];

            if (Array.isArray(_data["Errors"]) && _data["Errors"].length > 0) {
                this.errors = _data["Errors"].map(FileHandlerResponseError.fromJS);
            }

            if (Array.isArray(_data["Messages"]) && _data["Messages"].length > 0) {
                this.messages = _data["Messages"].map(FileHandlerResponseMessage.fromJS);
            }

            if (Array.isArray(_data["Results"]) && _data["Results"].length > 0) {
                this.results = _data["Results"]; // .map(FileHandlerResponseResult.fromJS);
            }
        }

        return this;
    }

    static fromJS(data: any): FileHandlerResponse {
        data = typeof data === 'object' ? data : {};
        return (new FileHandlerResponse()).init(data);
    }
}

export interface IFileHandlerResponse {
    success: boolean;
    processed: boolean;
    fileValid: boolean;
    errors: FileHandlerResponseError[];
    messages: FileHandlerResponseMessage[];
    results: any[]; // FileHandlerResponseResult[];
}

export class FileHandlerResponseError {
    // Gets or sets the user friendly error message.
    message: string;
    // Gets or sets detailed description of error.
    detailedMessage: string;

    init(_data?: any): this {
        if (_data) {
            trySetProp(this, _data, 'Message');
            trySetProp(this, _data, 'DetailedMessage');
        }

        return this;
    }

    static fromJS(data: any): FileHandlerResponseError {
        data = typeof data === 'object' ? data : {};
        return (new FileHandlerResponseError()).init(data);
    }
}

export class FileHandlerResponseMessage {
    message: string;

    init(_data?: any): this {
        if (_data) {
            trySetProp(this, _data, 'Message');
        }

        return this;
    }

    static fromJS(data: any): FileHandlerResponseMessage {
        data = typeof data === 'object' ? data : {};
        return (new FileHandlerResponseMessage()).init(data);
    }
}

export class FileHandlerResponseResult { }

export class PingResponse extends CommonBaseModel {
    ack!: number;

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;
        super.init(_data);
        return this;
    }

    static fromJS(data: any): PingResponse {
        data = typeof data === 'object' ? data : {};
        let result = new PingResponse();
        result.init(data);
        return result;
    }
}

export interface IPingResponse {
    ack: number;
}

export class FindSearchControlsResponse extends CommonBaseModel {
    success?: boolean;
    error?: string;
    sessionExpired?: boolean;
    data?: iApplyFormControl[];
}
